import { ESensorModel, type TBounds, type TXYPoint } from '@/types';
import { DEG_TO_RAD, RAD_TO_DEG, lerp, sumPoints } from './geometry';

export const PERSON_SIZE = 0.36;
export const MOUNT_ANGLE = 15;

/** Default FOV values for each sensor model */
export const DEFAULT_FOV_BY_MODEL: Record<ESensorModel, number> = {
  [ESensorModel.HEATIC1]: 60,
  [ESensorModel.HEATIC2]: 90,
  [ESensorModel.HEATIC2_PLUS]: 90,
  [ESensorModel.HEATIC3]: 120,
  [ESensorModel.UNKNOWN]: 90,
};

/** Gets the default FOV for a sensor model */
export const getFovForModel = (model: ESensorModel | string | undefined): number => {
  if (!model) return DEFAULT_FOV_BY_MODEL[ESensorModel.UNKNOWN];
  const enumModel = model as ESensorModel;
  return DEFAULT_FOV_BY_MODEL[enumModel] ?? DEFAULT_FOV_BY_MODEL[ESensorModel.UNKNOWN];
};

/** Gets the size of a sensor's coverage from a height and FOV number. */
export const calculateSensorCoverage = ({
  height,
  fov,
}: {
  height: number;
  fov: number;
}): number => {
  if (fov === 90) {
    return 0.84 * (2 * Math.tan(DEG_TO_RAD * (fov / 2)) * (height - 1.05) + PERSON_SIZE * 2);
  }
  return 2 * Math.tan(DEG_TO_RAD * (fov / 2)) * (height - 1.05) + PERSON_SIZE * 2;
};

/** Projects a point into wall coverage space. [0, 0] is the bottom left, [1, 1] is the top right
 * See math here: https://www.notion.so/butlrtech/The-calculation-for-theoretical-effective-coverage-of-wall-mounting-sensors-514261a66dfd4d2095349a366085a576?pvs=4
 * _______________________
 * \                     /
 *  \                   /
 *   \_________________/
 *            x sensor
 */
export const projectPointIntoWallCoverage = (
  p: TXYPoint,
  fov: number,
  height: number,
): TXYPoint => {
  const [px, py] = p;
  const efov = 2 * Math.atan(calculateSensorCoverage({ height, fov }) / (2 * height)) * RAD_TO_DEG;
  const angle = (MOUNT_ANGLE - efov / 2 + efov * py) * DEG_TO_RAD;
  const y = height * Math.tan(angle);
  const xRange = (2 * height * Math.sin((efov / 2) * DEG_TO_RAD)) / Math.cos(angle);
  const x = lerp(-xRange / 2, xRange / 2, px);
  return [x, y];
};

/**
 * Returns the sensor coverage in points (that form a trapezoid) for a wall-mounted sensor.
 */
export function calculateWallMountSensorCoverage(
  sensorCenter: TXYPoint,
  fov: number,
  height: number,
): TBounds {
  return {
    bottomLeft: sumPoints(sensorCenter, projectPointIntoWallCoverage([0, 0], fov, height)),
    bottomRight: sumPoints(sensorCenter, projectPointIntoWallCoverage([1, 0], fov, height)),
    topRight: sumPoints(sensorCenter, projectPointIntoWallCoverage([1, 1], fov, height)),
    topLeft: sumPoints(sensorCenter, projectPointIntoWallCoverage([0, 1], fov, height)),
  };
}

export function calculateCeilingMountSensorCorner(
  sensorCenter: TXYPoint,
  height: number,
): TXYPoint {
  const halfHeight = height / 2;
  return [sensorCenter[0] + halfHeight, sensorCenter[1] + halfHeight];
}
